import React from 'react';
import moment from 'moment';
import ActionsCell from '../../../../common/cells/ActionsCell';
import DateCell from '../../../../common/cells/DateCell';
import { DATE_FORMAT, TIME_FORMAT } from '../../../../../config/locale';
import BadgeList from '../../../../common/BadgeList';
import IsActive from '../../../../common/cells/IsActive';
import {
  ITINERARY_FOR_COUNTER_PATH,
  ITINERARY_PATH,
} from '../../../../../config/paths';
import LinkToModuleResource from '../../../../common/resource/LinkToModuleResource';
import { ITINERARY_SCHEDULING_COLUMN_OPTIONS } from '../../../../../config/constants';
import SelectFilter from '../../../../common/filters/SelectFilter';
import Badge from '../../../../common/Badge';
import { filterMethodCommaSeparatedValues } from '../../../../../utils/filter-methods';

const ITINERARY_SCHEDULING_ALL_COLUMN_IDS =
  ITINERARY_SCHEDULING_COLUMN_OPTIONS.map((opt) => opt.value);

const formatMinutes = (minutes) => {
  if (!minutes && minutes !== 0) return '-';

  const dur = moment.duration(minutes, 'minutes');

  const hours = Math.floor(dur.asHours());
  const mins = dur.minutes();

  if (hours > 0) {
    return `${hours} h ${mins} m`;
  }

  return `${mins} m`;
};

const generateItinerariesForSchedulingTableColumns = ({
  handleConfirm,
  handleClear,
  handleConfirmCabineCrew,
  handleClearCabineCrew,
  handleConfirmFood,
  handleClearFood,
  additionalButton,
  visibleColumnIds,
}) => {
  const columns = [
    {
      id: 'actions-bus',
      Header: 'Bus',
      className: 'text-center',
      Cell: (row) => (
        <ActionsCell
          handleConfirm={handleConfirm}
          handleClear={handleClear}
          data={row.original}
          id={row.index}
          showClearButton={
            !!row.original.highligth || !!row.original.highlightSuggestion
          }
          showConfirmButton={
            !row.original.tripId &&
            row.original.active &&
            !row.original.highlightSuggestion
          }
          additionalButton={additionalButton(row.original)}
        />
      ),
      width: 100,
      filterable: false,
    },
    {
      id: 'actions-cabin-crew',
      Header: 'Servicio a Bordo',
      className: 'text-center',
      Cell: (row) => (
        <ActionsCell
          handleConfirm={handleConfirmCabineCrew}
          handleClear={handleClearCabineCrew}
          data={row.original}
          id={row.index}
          showClearButton={row.original.highligthCrewCabine}
          showConfirmButton={Boolean(row.original.tripId)}
          confirmIcon="fa-user-plus"
        />
      ),
      width: 100,
      filterable: false,
    },
    {
      id: 'actions-alimentacion',
      Header: 'Alimentación',
      className: 'text-center',
      Cell: (row) => (
        <ActionsCell
          handleConfirm={handleConfirmFood}
          handleClear={handleClearFood}
          data={row.original}
          id={row.index}
          showClearButton={row.original.highligthAlimentacion}
          showConfirmButton={
            row.original.active &&
            (!Array.isArray(row.original.itineraFoodList) ||
              row.original.itineraFoodList.length === 0)
          }
          confirmIcon="fa-cutlery"
        />
      ),
      width: 130,
      filterable: false,
    },
    {
      id: 'departuretime',
      Header: 'Fecha',
      accessor: 'departuretime',
      Cell: (row) => <DateCell value={row.value} format={DATE_FORMAT} />,
      className: 'text-center',
      minWidth: 120,
      filterable: false,
    },
    {
      id: 'sourceLocationName',
      Header: 'Origen',
      accessor: 'sourceLocationName',
      className: 'text-center',
      minWidth: 250,
    },
    {
      id: 'destinationLocationName',
      Header: 'Destino',
      accessor: 'destinationLocationName',
      className: 'text-center',
      minWidth: 250,
    },
    {
      id: 'departuretime-hour',
      Header: 'Hora',
      accessor: 'departuretime',
      Cell: (row) => <DateCell value={row.value} format={TIME_FORMAT} />,
      className: 'text-center',
      minWidth: 80,
      filterable: false,
    },
    {
      Header: 'Hora de Salida en Base',
      id: 'departureTimeAtBase',
      accessor: 'departureTimeAtBase',
      Cell: (row) => <DateCell value={row.value} format={TIME_FORMAT} />,
      className: 'text-center',
      minWidth: 190,
      filterable: false,
    },
    {
      id: 'waitTimeViolation',
      Header: 'Tiempo de Permanencia Fallido (min)',
      accessor: 'waitTimeViolation',
      className: 'text-center',
      minWidth: 150,
      filterable: false,
      Cell: (row) => {
        if (!row.value) return '-';

        const source = row.original.highlightSuggestion
          ? row.value
          : row.original;
        const { waitTimeViolation, respectsWaitTime, previousItineraryId } =
          source;

        return (
          <span className={respectsWaitTime ? 'text-success' : 'text-warning'}>
            <i
              className={
                respectsWaitTime ? 'fa fa-check' : 'fa fa-exclamation-triangle'
              }
            />{' '}
            {formatMinutes(waitTimeViolation)}{' '}
            <LinkToModuleResource
              text=""
              href={`${ITINERARY_FOR_COUNTER_PATH}/${previousItineraryId}`}
              isExternalPath
            />
          </span>
        );
      },
    },
    {
      id: 'respectsWaitTime',
      Header: 'Respeta Tiempo de Permanencia',
      accessor: 'waitTimeViolation',
      className: 'text-center',
      minWidth: 150,
      Cell: (row) => {
        if (!row.value) return '-';

        const source = row.original.highlightSuggestion
          ? row.value
          : row.original;

        const { respectsWaitTime } = source;

        return (
          <Badge
            text={respectsWaitTime ? 'Sí' : 'No'}
            color={respectsWaitTime ? 'success' : 'warning'}
          />
        );
      },
      filterMethod: (filter, row) => {
        if (!filter.value || filter.value === 'all') return true;

        // eslint-disable-next-line no-underscore-dangle
        const source = row._original.highlightSuggestion
        // eslint-disable-next-line no-underscore-dangle
          ? row._original.waitTimeViolation
          // eslint-disable-next-line no-underscore-dangle
          : row._original;

        if (!source) return true;

        if (filter.value === 'true') return source.respectsWaitTime === true;
        if (filter.value === 'false') return source.respectsWaitTime === false;
        return true;
      },
      // eslint-disable-next-line react/prop-types
      Filter: ({ onChange }) => (
        <SelectFilter
          onChangeFilter={(value) =>
            onChange(value !== null ? String(value) : 'all')
          }
          options={[
            { value: 'all', label: 'Todos' },
            { value: true, label: 'Sí' },
            { value: false, label: 'No' },
          ]}
          placeholder="Todos"
          isClearable
        />
      ),
    },
    {
      id: 'serviceType',
      Header: 'Tipo Servicio',
      accessor: 'serviceType',
      className: 'text-center',
      minWidth: 180,
    },
    {
      id: 'passengerSeatCapacity',
      Header: 'Capacidad de Asiento',
      accessor: 'passengerSeatCapacity',
      Cell: (row) => (row.value || row.value === 0 ? row.value : '-'),
      className: 'text-center',
      minWidth: 150,
      filterMethod: filterMethodCommaSeparatedValues,
    },
    {
      id: 'companyBusId',
      Header: 'Bus',
      accessor: 'companyBusId',
      Cell: (row) => (row.value ? row.value.label : '-'),
      className: 'text-center',
      minWidth: 100,
      filterMethod: (filter, row) =>
        filterMethodCommaSeparatedValues(filter, {
          [filter.id]: row[filter.id] ? row[filter.id].label : null,
        }),
    },
    {
      id: 'busChangeCount',
      Header: 'Cant. Cambios',
      accessor: 'busChangeCount',
      Cell: (row) => (row.value || row.value === 0 ? row.value : '-'),
      className: 'text-center',
      minWidth: 100,
      filterable: false,
    },
    {
      id: 'changedBuses',
      Header: 'Buses Cambiados',
      accessor: 'changedBuses',
      Cell: (row) => (row.value ? row.value : '-'),
      className: 'text-center',
      minWidth: 150,
      filterMethod: filterMethodCommaSeparatedValues,
    },
    {
      id: 'firstDriverName',
      Header: 'Conductor 1',
      accessor: 'firstDriverName',
      className: 'text-center',
      minWidth: 200,
      Cell: (row) => (row.value ? row.value : '-'),
    },
    {
      id: 'secondDriverName',
      Header: 'Conductor 2',
      accessor: 'secondDriverName',
      className: 'text-center',
      minWidth: 200,
      Cell: (row) => (row.value ? row.value : '-'),
    },
    {
      id: 'thirdDriverName',
      Header: 'Conductor 3',
      accessor: 'thirdDriverName',
      className: 'text-center',
      minWidth: 200,
      Cell: (row) => (row.value ? row.value : '-'),
    },
    {
      accessor: 'cabinCrewList',
      Header: 'Tripulantes',
      id: 'cabinCrewList',
      Cell: (row) => {
        if (!row.value) return null;
        const cabinCrewList = row.value.map(({ cabinCrewName }) => ({
          text: cabinCrewName,
        }));
        return (
          <BadgeList
            cell
            textArray={cabinCrewList.length > 0 ? cabinCrewList : []}
          />
        );
      },
      className: 'text-center',
      filterable: false,
    },
    {
      id: 'tripCircuitStatus',
      Header: 'Orden en Circuito',
      accessor: 'tripCircuitStatus',
      Cell: (row) => (row.value ? row.value.label : '-'),
      className: 'text-center',
      minWidth: 150,
      filterable: false,
    },
    {
      id: 'active',
      Header: 'Estado',
      accessor: 'active',
      Cell: (row) => <IsActive value={row.value} />,
      className: 'text-center',
      minWidth: 100,
      filterMethod: (filter, row) => {
        if (!filter.value || filter.value === 'all') return true;
        if (filter.value === 'true') return row.active === true;
        if (filter.value === 'false') return row.active === false;
        return true;
      },
      // eslint-disable-next-line react/prop-types
      Filter: ({ onChange }) => (
        <SelectFilter
          onChangeFilter={(value) =>
            onChange(value !== null ? String(value) : 'all')
          }
          options={[
            { value: 'all', label: 'Todos' },
            { value: true, label: 'Activo' },
            { value: false, label: 'Inactivo' },
          ]}
          placeholder="Todos"
          isClearable
        />
      ),
    },
    {
      id: 'ticketsSold',
      Header: 'Boletos Vendidos',
      accessor: 'ticketsSold',
      className: 'text-center',
      minWidth: 150,
    },
    {
      id: 'type',
      Header: 'Tipo de Itinerario',
      accessor: 'type',
      className: 'text-center',
      minWidth: 250,
    },
    {
      id: 'routeName',
      Header: 'Ruta',
      accessor: 'routeName',
      className: 'text-center',
      minWidth: 400,
    },
    {
      id: 'id',
      Header: 'ID',
      accessor: 'id',
      Cell: (row) => (
        <>
          {row.value}
          <LinkToModuleResource
            text=""
            href={`${ITINERARY_PATH}/${row.value}`}
            isExternalPath
            colorIcon="text-danger"
          />
          <LinkToModuleResource
            text=""
            href={`${ITINERARY_FOR_COUNTER_PATH}/${row.value}`}
            isExternalPath
          />
        </>
      ),
      className: 'text-center',
      minWidth: 150,
    },
  ];

  if (!visibleColumnIds || !visibleColumnIds.length) {
    return columns;
  }

  const visibleSet = new Set(visibleColumnIds);
  const togglableSet = new Set(ITINERARY_SCHEDULING_ALL_COLUMN_IDS);

  return columns.filter((col) => {
    if (!col.id) return true;

    if (!togglableSet.has(col.id)) return true;

    return visibleSet.has(col.id);
  });
};

export default generateItinerariesForSchedulingTableColumns;
